import _ from 'lodash';
import moment from 'moment';

import marketService from '@/services/market';
import random from '@/utils/random';
import conrad from '@ibiquity/dashboard.conrad';
import axiosInterceptor from '@ibiquity/dashboard.rollbar/src/axiosInterceptor';
import Rollbar from '@ibiquity/dashboard.rollbar';

axiosInterceptor.install(conrad.conradClient, {
  ignoredUrls: [/v1\/broadcasts\/[\d]+\/live/],
  level: 'warning',
});

/* eslint-disable no-param-reassign, no-shadow, import/no-named-as-default-member */
const state = {
  markets: {},
  broadcasts: {},
  tiles: {},
  location: { lng: 1.000, lat: 1.000 },
  error: null,
  subscribedBroadcastId: null,
  liveEventsInitialized: false,
};

const getters = {
  markets(state) {
    return Object.values(state.markets);
  },
  marketTiles(state) {
    return marketId => state.markets[marketId].tileIds
      .map(id => state.tiles[id]);
  },
  availalbeMarketTiles(state, getters) {
    return (marketId) => {
      const market = state.markets[marketId];
      return getters
        .marketTiles(marketId)
        .filter((tile) => {
          const { liveDataUpdatedAt } = state.broadcasts[tile.broadcastId] || {};
          return tile.id !== market.lastUpdated
            && (!liveDataUpdatedAt || moment().diff(liveDataUpdatedAt, 'seconds') > 5);
        });
    };
  },
  availalbeMarketBroadcasts(state, getters) {
    return (marketId) => {
      const taken = getters.marketTiles(marketId).map(item => item.broadcastId);
      return getters.marketBroadcasts(marketId)
        .filter(({ id, loaded }) => !taken.includes(id) && loaded);
    };
  },
  broadcast(state) {
    return broadcastId => state.broadcasts[broadcastId];
  },
  marketBroadcasts(state) {
    return marketId => state.markets[marketId].broadcastIds
      .map(id => state.broadcasts[id]);
  },
};

const actions = {
  async loadMarkets({ commit }, marketIds) {
    const markets = await marketService.getMarkets(marketIds);

    const broadcasts = _(markets).uniqBy('name').flatMap('broadcasts').value();
    markets.forEach((market) => {
      market.id = market.id || random.id();
      market.tileIds = [];
      market.broadcastIds = market.broadcasts.map(broadcast => broadcast.id);
      delete market.broadcasts;
    });
    commit('setMarkets', _.keyBy(markets, 'id'));
    commit('setBroadcasts', _.keyBy(broadcasts, 'id'));
  },
  createTiles({ getters, commit, state }, { marketId, tileCount }) {
    const broadcastIds = _(state.markets[marketId].broadcastIds)
      .shuffle()
      .map(getters.broadcast)
      .filter(['loaded', true])
      .map('id')
      .take(tileCount)
      .value();
    if (broadcastIds.length !== tileCount) {
      const message = `Market ${marketId} doesn't have enough broadcasts to create tiles`;
      commit('setError', { message });
      return;
    }
    const tiles = _(broadcastIds)
      .map(broadcastId => ({ id: random.id(), broadcastId }))
      .keyBy('id')
      .value();
    commit('addTiles', tiles);
    commit('updateMarket', { marketId, data: { tileIds: Object.keys(tiles) } });
  },
  async loadTile({ state, dispatch, commit }, tileId) {
    commit('updateTile', { tileId, data: { loading: true } });
    await dispatch('loadLiveBroadcast', state.tiles[tileId].broadcastId);
    commit('updateTile', { tileId, data: { loading: false } });
  },
  updateRandomTile({ commit, getters }, { marketId, frozenBroadcastId }) {
    const availalbleTiles = getters.availalbeMarketTiles(marketId);
    const selectedTile = random.item(
      availalbleTiles.filter(tile => tile.broadcastId !== frozenBroadcastId),
    );
    if (!selectedTile) {
      commit('setError', { message: 'Unable to update a random tile: lack of available tiles' });
      return;
    }
    const broadcasts = getters.availalbeMarketBroadcasts(marketId);
    if (!broadcasts.length) {
      commit('setError', { message: 'Unable to update a random tile: lack of free broadcasts' });
      return;
    }
    const selectedbroadcast = broadcasts[random.weightedIndex(broadcasts)];
    commit('updateTile', {
      tileId: selectedTile.id,
      data: { broadcastId: selectedbroadcast.id },
    });
    commit('updateMarket', { marketId, data: { lastUpdated: selectedTile.id } });
  },
  async loadBroadcasts({ state, commit, dispatch }, marketId) {
    try {
      const { broadcastIds } = state.markets[marketId];
      await Promise.all(broadcastIds.map(id => dispatch('loadBroadcast', id)));
    } catch (error) {
      commit('setError', {
        message: `Fetching Conrad data failed: ${error.message || error}`,
        level: 'warning',
      });
    }
  },
  async loadBroadcast({ commit, dispatch }, broadcastId) {
    try {
      const { data } = await conrad.getBroadcast(broadcastId);
      commit('updateBroadcast', { broadcastId, data: { ...data, loaded: true } });
      dispatch('preloadImage', { url: data.logoUrl, keep: true });
    } catch (error) {
      commit('setError', {
        message: `Fetching Conrad data failed: ${error.message || error}`,
        level: 'warning',
      });
    }
  },
  async loadLiveBroadcast({ state, commit, dispatch }, broadcastId) {
    if (!state.broadcasts[broadcastId].hasLiveData) return;

    try {
      const { data } = await conrad.getLiveBroadcast(
        broadcastId, state.location,
        { delay: process.env.VUE_APP_LIVE_BROADCAST_POLLING_DELAY },
      );
      const liveDataUpdatedAt = new Date().toISOString();
      commit('updateBroadcast', { broadcastId, data: { ...data, liveDataUpdatedAt } });
      await dispatch('preloadImage', { url: data.liveImageUrl });
    } catch (error) {
      commit('setError', {
        message: `Unable to load broadcast live data for ${broadcastId}`,
        level: 'info',
      });
    }
  },
  async subscribe({ state, commit, dispatch }, broadcastId) {
    if (!state.liveEventsInitialized) {
      const handler = broadcastId => dispatch('loadLiveBroadcast', broadcastId);
      conrad.liveEvents.addHandler('liveEvent', handler);
      commit('setLiveEventsInitialized', true);
    }
    if (state.subscribedBroadcastId) {
      await dispatch('unsubscribe');
    }
    conrad.liveEvents.subscribe(broadcastId);
    commit('setSubscribedBroadcastId', broadcastId);
  },
  unsubscribe({ state, commit }) {
    const subject = state.subscribedBroadcastId;
    conrad.liveEvents.unsubscribe(subject);
    commit('setSubscribedBroadcastId', null);
  },
  preloadImage({ dispatch }, { url, keep = false } = {}) {
    return dispatch('prefetcher/load', { url, keep }, { root: true })
      .then((asset) => {
        if (asset && asset.request) return asset.request;
        return false;
      });
  },
};

const mutations = {
  setMarkets(state, markets) {
    state.markets = markets;
  },
  updateMarket(state, { marketId, data }) {
    state.markets[marketId] = { ...state.markets[marketId], ...data };
  },
  setBroadcasts(state, broadcasts) {
    state.broadcasts = broadcasts;
  },
  updateBroadcast(state, { broadcastId, data }) {
    state.broadcasts[broadcastId] = { ...state.broadcasts[broadcastId], ...data };
  },
  addTiles(state, tiles) {
    state.tiles = { ...state.tiles, ...tiles };
  },
  updateTile(state, { tileId, data }) {
    state.tiles[tileId] = { ...state.tiles[tileId], ...data };
  },
  setLiveEventsInitialized(state, value) {
    state.liveEventsInitialized = value;
  },
  setSubscribedBroadcastId(state, value) {
    state.subscribedBroadcastId = value;
  },
  setError(state, { message, level }) {
    state.error = message;
    if (state.error && level) Rollbar[level](state.error);
  },
};
/* eslint-enable no-param-reassign, no-shadow, import/no-named-as-default-member */

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
